<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onScopeDispose,
    ref,
    watch,
  } from 'vue';
  import { useNow } from '@vueuse/core';
  import { ReservationsService } from '@/api/services/reservations.service';
  import V2ReservationTablePickerModal from '@/components/modals/reservations/V2ReservationTablePickerModal.vue';
  import type { IReservationTablePickerResult } from '@/interfaces/IReservationUi';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { useReservationsWaitlist } from '@/composition/reservations/use-reservations-waitlist';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { getReservationTimeZone } from '@/domain/reservations/reservation-time';
  import { AlertType } from '@/interfaces/IAlert';
  import type { IReservation } from '@/interfaces/IReservation';
  import type { IReservationWaitlist } from '@/interfaces/IReservationWaitlist';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import type { IReservationFormProps } from './reservation-form.interfaces';
  import V2ReservationsWaitlistRow from './V2ReservationsWaitlistRow.vue';

  const emit = defineEmits<{
    (event: 'add'): void;
    (event: 'count', count: number): void;
    (event: 'create', prefill: IReservationFormProps): void;
    (event: 'select', reservation: IReservation): void;
  }>();
  const {
    inHouse,
    online,
    waitingCount,
    loading,
    ready,
    error,
    busy,
    actionErrors,
    load,
    notify,
    cancel,
    seat,
  } = useReservationsWaitlist();
  const restaurant = useRestaurantStore();
  const modals = useModal();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;
  const now = useNow({ interval: 30_000 });
  const opening = ref<number | null>(null);
  const zone = computed(() =>
    getReservationTimeZone(
      restaurant.selectedBranch?.location,
      restaurant.restaurant?.country
    )
  );
  const country = computed(() => restaurant.restaurant?.country ?? 'AR');
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  let alive = true;
  let scopeEpoch = 0;
  watch(
    () => [restaurant.restaurant?.id, restaurant.selectedBranch?.id],
    () => {
      ++scopeEpoch;
      opening.value = null;
    }
  );
  onScopeDispose(() => {
    alive = false;
  });
  const current = (entry: IReservationWaitlist) =>
    alive &&
    entry.restaurantId === restaurant.restaurant?.id &&
    entry.branchId === restaurant.selectedBranch?.id;
  watch(waitingCount, (value) => emit('count', value), { immediate: true });
  async function notifyEntry(entry: IReservationWaitlist) {
    try {
      const result = await notify(entry);
      if (result && current(entry))
        ui.showToast(AlertType.Success, tr('reservations.waitlist.notifySent'));
    } catch (cause) {
      if (current(entry))
        ui.serverError(cause, tr('reservations.waitlist.notifyError'));
    }
  }
  async function removeEntry(entry: IReservationWaitlist) {
    if (!current(entry) || opening.value != null) return;
    const epoch = scopeEpoch;
    opening.value = entry.id;
    const accepted = await modals.confirm({
      title: tr('reservations.waitlist.removeTitle'),
      message: tr('reservations.waitlist.removeMessage', {
        name: entry.guestName,
      }),
      acceptText: tr('reservations.waitlist.remove'),
      cancelText: tr('cancel'),
      acceptColor: 'error',
    });
    if (!current(entry) || epoch !== scopeEpoch) return;
    opening.value = null;
    if (!accepted) return;
    try {
      const result = await cancel(entry);
      if (result && current(entry))
        ui.showToast(AlertType.Success, tr('reservations.waitlist.removed'));
    } catch (cause) {
      if (current(entry))
        ui.serverError(cause, tr('reservations.detail.saveError'));
    }
  }
  async function seatEntry(entry: IReservationWaitlist) {
    if (!current(entry) || opening.value != null) return;
    const epoch = scopeEpoch;
    opening.value = entry.id;
    try {
      const { settings } = await ReservationsService.getBookingContext(
        entry.restaurantId,
        entry.branchId
      );
      if (!current(entry) || epoch !== scopeEpoch) return;
      const durationMin =
        settings.durationRules.find((rule) => entry.partySize <= rule.maxParty)
          ?.minutes ?? settings.defaultDurationMin;
      const selection = await modals.showDynamicDialog<IReservationTablePickerResult | null>(
        V2ReservationTablePickerModal,
        {
          reservation: {
            id: 0,
            partySize: entry.partySize,
            startsAt: new Date().toISOString(),
            durationMin,
            tableIds: [],
          },
          mode: 'now',
        },
        'v2-reservation-tables',
        { width: 620, clickToClose: false }
      );
      if (!current(entry) || epoch !== scopeEpoch) return;
      opening.value = null;
      if (!selection) return;
      const result = await seat(entry, { tableIds: selection.tableIds });
      if (result && current(entry)) emit('select', result.reservation);
    } catch (cause) {
      if (current(entry) && epoch === scopeEpoch) {
        opening.value = null;
        ui.serverError(cause, tr('reservations.walkIn.error'));
      }
    }
  }
  function newBooking(entry: IReservationWaitlist) {
    if (!current(entry)) return;
    emit('create', {
      partySize: entry.partySize,
      startsAt: entry.desiredStartsAt ?? undefined,
      date: entry.desiredDate ?? undefined,
      guest: {
        customerId: entry.customerId,
        name: entry.guestName,
        phoneE164: entry.guestPhone,
        email: entry.guestEmail,
      },
    });
  }
  defineExpose({ reload: load });
</script>

<template>
  <div class="reservations-waitlist">
    <div class="waitlist-card">
      <div class="waitlist-toolbar">
        <span>{{
          $t('reservations.waitlist.count', { count: waitingCount })
        }}</span
        ><PosUiButton icon="plus" @click="emit('add')">{{
          $t('reservations.waitlist.add')
        }}</PosUiButton>
      </div>
      <div class="waitlist-content">
        <div
          v-if="loading && !ready"
          class="waitlist-skeletons"
          aria-busy="true"
        >
          <PosUiSkeleton v-for="index in 4" :key="index" class="row-skeleton" />
        </div>
        <PosUiEmptyState
          v-else-if="error && !ready"
          icon="alert-circle-outline"
          :title="$t('reservations.waitlist.loadError')"
          ><template #action
            ><PosUiButton
              color="neutral"
              variant="outline"
              icon="refresh"
              @click="load"
              >{{ $t('reservations.page.retry') }}</PosUiButton
            ></template
          ></PosUiEmptyState
        >
        <template v-else>
          <div v-if="error" class="refresh-error" role="alert">
            <span>{{ $t('reservations.waitlist.loadError') }}</span
            ><PosUiButton
              color="neutral"
              variant="outline"
              icon="refresh"
              @click="load"
              >{{ $t('reservations.page.retry') }}</PosUiButton
            >
          </div>
          <section class="waitlist-section">
            <header>
              <PosUiIcon name="storefront-outline" />
              <h2>{{ $t('reservations.waitlist.inHouse') }}</h2>
              <span class="section-count">{{ inHouse.length }}</span>
            </header>
            <PosUiEmptyState
              v-if="!inHouse.length"
              icon="account-clock-outline"
              :title="$t('reservations.waitlist.emptyTitle')"
              :description="$t('reservations.waitlist.emptyDescription')"
              ><template #action
                ><PosUiButton
                  color="neutral"
                  variant="outline"
                  icon="plus"
                  @click="emit('add')"
                  >{{ $t('reservations.waitlist.add') }}</PosUiButton
                ></template
              ></PosUiEmptyState
            >
            <div v-else class="waitlist-rows">
              <V2ReservationsWaitlistRow
                v-for="entry in inHouse"
                :key="entry.id"
                :entry="entry"
                :now="now"
                :time-zone="zone"
                :business-day-start-hour="
                  restaurant.selectedBranch?.businessDayStartHour
                "
                :country="country"
                :busy="
                  busy[entry.id] || (opening === entry.id ? 'seat' : undefined)
                "
                :error="actionErrors[entry.id]"
                @notify="notifyEntry(entry)"
                @seat="seatEntry(entry)"
                @remove="removeEntry(entry)"
              />
            </div>
          </section>
          <section class="waitlist-section">
            <header>
              <PosUiIcon name="bell-outline" />
              <h2>{{ $t('reservations.waitlist.online') }}</h2>
              <span class="section-count">{{ online.length }}</span>
            </header>
            <PosUiEmptyState
              v-if="!online.length"
              icon="bell-sleep-outline"
              :title="$t('reservations.waitlist.onlineEmpty')"
            />
            <div v-else class="waitlist-rows">
              <V2ReservationsWaitlistRow
                v-for="entry in online"
                :key="entry.id"
                :entry="entry"
                :now="now"
                :time-zone="zone"
                :business-day-start-hour="
                  restaurant.selectedBranch?.businessDayStartHour
                "
                :country="country"
                :busy="
                  busy[entry.id] ||
                  (opening === entry.id ? 'cancel' : undefined)
                "
                :error="actionErrors[entry.id]"
                @create="newBooking(entry)"
                @remove="removeEntry(entry)"
              />
            </div>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // Content page: a padded, centred white card (see the V2 layout rules).
  .reservations-waitlist {
    @apply tw-mx-auto tw-flex tw-h-full tw-min-h-0 tw-w-full tw-max-w-7xl tw-flex-col tw-p-4 md:tw-p-6;
    .waitlist-card {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
    }
    .waitlist-toolbar {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-3;
      > span {
        @apply tw-text-sm tw-tabular-nums tw-text-neutral-500;
      }
    }
    .waitlist-content {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-5 tw-overflow-y-auto tw-bg-canvas tw-p-3 md:tw-p-4;
    }
    .waitlist-section {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
      > header {
        @apply tw-flex tw-items-center tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-px-4 tw-py-3 tw-text-neutral-500;
        h2 {
          @apply tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900;
        }
      }
    }
    .section-count {
      @apply tw-ml-auto tw-rounded-full tw-bg-neutral-100 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-tabular-nums tw-text-neutral-600;
    }
    .waitlist-rows {
      @apply tw-grid tw-gap-3 tw-bg-canvas tw-p-3;
    }
    .waitlist-skeletons {
      @apply tw-grid tw-gap-3;
    }
    .row-skeleton {
      @apply tw-h-32 tw-w-full;
    }
    .refresh-error {
      @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-red-200 tw-bg-white tw-p-3 tw-text-sm tw-text-red-600;
    }
  }
</style>
